import { useState, useRef, useEffect } from 'react';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';

interface DatePickerProps {
  value?: string; // Format: YYYY-MM-DD or YYYY-MM
  onChange: (value: string) => void;
  placeholder?: string;
  mode?: 'date' | 'month'; // 'date' for YYYY-MM-DD, 'month' for YYYY-MM
}

const DatePicker = ({ value, onChange, placeholder = '연도-월-일', mode = 'date' }: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      // For month mode, append '-01' to make it a valid date
      const dateValue = mode === 'month' && value.length === 7 ? `${value}-01` : value;
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  });
  
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const dateValue = mode === 'month' && value.length === 7 ? `${value}-01` : value;
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    return new Date();
  });
  
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const dateValue = mode === 'month' && value.length === 7 ? `${value}-01` : value;
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setCurrentMonth(date);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value, mode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  
  const days = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
    if (mode === 'month') {
      // For month mode, only return YYYY-MM
      const monthValue = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
      onChange(monthValue);
    } else {
      // For date mode, return YYYY-MM-DD
      const dateValue = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      onChange(dateValue);
    }
    setIsOpen(false);
  };

  const handleMonthClick = (monthIndex: number) => {
    if (mode === 'month') {
      // For month mode, select the first day of the month
      const newDate = new Date(currentMonth.getFullYear(), monthIndex, 1);
      setSelectedDate(newDate);
      const monthValue = `${newDate.getFullYear()}-${String(monthIndex + 1).padStart(2, '0')}`;
      onChange(monthValue);
      setIsOpen(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today);
    if (mode === 'month') {
      const monthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      onChange(monthValue);
    } else {
      const dateValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      onChange(dateValue);
    }
    setIsOpen(false);
  };

  const displayValue = selectedDate
    ? mode === 'month'
      ? `${selectedDate.getFullYear()}년 ${selectedDate.getMonth() + 1}월`
      : `${selectedDate.getFullYear()}년 ${selectedDate.getMonth() + 1}월 ${selectedDate.getDate()}일`
    : '';

  const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  const isToday = (day: number) => {
    const today = new Date();
    return (
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      currentMonth.getFullYear() === selectedDate.getFullYear() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      day === selectedDate.getDate()
    );
  };

  const isMonthSelected = (monthIndex: number) => {
    if (!selectedDate) return false;
    return (
      currentMonth.getFullYear() === selectedDate.getFullYear() &&
      selectedDate.getMonth() === monthIndex
    );
  };

  return (
    <Container ref={containerRef} mode={mode}>
      <InputWrapper onClick={() => setIsOpen(!isOpen)}>
        <Input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          mode={mode}
        />
        <CalendarIcon>📅</CalendarIcon>
      </InputWrapper>
      
      {isOpen && (
        <Dropdown style={{ minWidth: mode === 'month' ? '16rem' : '16rem', width: 'auto' }}>
          {mode === 'month' ? (
            <>
              <YearSelector>
                <YearButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1))}>‹</YearButton>
                <YearInput
                  type="number"
                  value={currentMonth.getFullYear()}
                  onChange={(e) => setCurrentMonth(new Date(parseInt(e.target.value) || currentMonth.getFullYear(), currentMonth.getMonth(), 1))}
                />
                <YearButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1))}>›</YearButton>
              </YearSelector>
              
              <MonthGrid>
                {months.map((month, index) => (
                  <MonthButtonCell
                    key={index}
                    isSelected={isMonthSelected(index)}
                    onClick={() => handleMonthClick(index)}
                  >
                    {month}
                  </MonthButtonCell>
                ))}
              </MonthGrid>
              <ButtonRow>
                <ActionButton onClick={handleClear}>지우기</ActionButton>
                <ActionButton onClick={handleToday}>이번 달</ActionButton>
              </ButtonRow>
            </>
          ) : (
            <>
              <Header>
                <MonthButton onClick={handlePreviousMonth}>‹</MonthButton>
                <MonthYear>
                  {currentMonth.getFullYear()}년 {months[currentMonth.getMonth()]}
                </MonthYear>
                <MonthButton onClick={handleNextMonth}>›</MonthButton>
              </Header>
              
              <WeekDays>
                {weekDays.map((day) => (
                  <WeekDay key={day}>{day}</WeekDay>
                ))}
              </WeekDays>
              
              <DaysGrid>
                {days.map((day, index) => (
                  <DayCell
                    key={index}
                    isEmpty={day === null}
                    isToday={day !== null && isToday(day)}
                    isSelected={day !== null && isSelected(day)}
                    onClick={() => day !== null && handleDateClick(day)}
                  >
                    {day}
                  </DayCell>
                ))}
              </DaysGrid>
              
              <ButtonRow>
                <ActionButton onClick={handleClear}>지우기</ActionButton>
                <ActionButton onClick={handleToday}>오늘</ActionButton>
              </ButtonRow>
            </>
          )}
        </Dropdown>
      )}
    </Container>
  );
};

const Container = styled.div<{ mode?: 'date' | 'month' }>`
  position: relative;
  width: ${props => props.mode === 'date' ? '14rem' : '9rem'};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input<{ mode?: 'date' | 'month' }>`
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 0.75rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  background-color: white;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:focus {
    outline: none;
    border-color: ${Theme.colors.B_1};
  }
`;

const CalendarIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  pointer-events: none;
  font-size: 1rem;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  background: white;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 16rem;
  padding: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const MonthYear = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${Theme.colors.G_1};
`;

const MonthButton = styled.button`
  width: 2rem;
  height: 2rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  background: white;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${Theme.colors.G_6};
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${Theme.colors.G_2};
  padding: 0.25rem;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const DayCell = styled.button<{ isEmpty: boolean; isToday: boolean; isSelected: boolean }>`
  padding: 0.5rem;
  border: 0.0625rem solid ${props => {
    if (props.isSelected) return Theme.colors.B_1;
    if (props.isToday) return Theme.colors.B_3;
    return 'transparent';
  }};
  border-radius: 0.25rem;
  background: ${props => {
    if (props.isSelected) return Theme.colors.B_1;
    if (props.isToday) return Theme.colors.B_3_light;
    return 'transparent';
  }};
  color: ${props => {
    if (props.isEmpty) return 'transparent';
    if (props.isSelected) return 'white';
    if (props.isToday) return Theme.colors.B_1;
    return Theme.colors.G_1;
  }};
  cursor: ${props => props.isEmpty ? 'default' : 'pointer'};
  font-size: 0.875rem;
  font-weight: ${props => props.isSelected || props.isToday ? 600 : 400};
  
  &:hover:not(:disabled) {
    background-color: ${props => {
      if (props.isEmpty) return 'transparent';
      if (props.isSelected) return Theme.colors.B_2;
      return Theme.colors.G_6;
    }};
  }
  
  &:disabled {
    cursor: default;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${Theme.colors.G_6};
  }
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  width: 100%;
  
  /* Ensure grid doesn't wrap */
  min-width: 0;
`;

const MonthButtonCell = styled.button<{ isSelected: boolean }>`
  padding: 0.5rem;
  border: 0.0625rem solid ${props => props.isSelected ? Theme.colors.B_1 : Theme.colors.G_3};
  border-radius: 0.25rem;
  background: ${props => props.isSelected ? Theme.colors.B_1 : 'white'};
  color: ${props => props.isSelected ? 'white' : Theme.colors.G_1};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${props => props.isSelected ? 600 : 400};
  min-width: 0;
  width: 100%;
  
  &:hover {
    background-color: ${props => props.isSelected ? Theme.colors.B_2 : Theme.colors.G_6};
  }
`;

const YearSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const YearInput = styled.input`
  width: 5rem;
  padding: 0.25rem 0.5rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  text-align: center;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: ${Theme.colors.B_1};
  }
`;

const YearButton = styled.button`
  width: 2rem;
  height: 2rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  background: white;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${Theme.colors.G_6};
  }
`;

export default DatePicker;

